import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { ISnowApplicationState } from './snowApplication.model';
import {
  deleteSnowApplication,
  getSnowApplicationById,
  processDataSnow,
  saveSnowApplication,
  searchSnowApplication,
} from './snowApplication.action';
import { ISnowApplication } from '../../../services/snow/snowApplication/snowApplication.model';

export const initialState: ISnowApplicationState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  processData: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const snowApplicationSlice = createSlice({
  name: 'snowApplication',
  initialState,
  reducers: {
    clearSnowApplication: () => {
      return initialState;
    },
    clearSnowApplicationMessages: (state) => {
      state.save.messages = [];
      state.processData.messages = [];
      state.delete.messages = [];
    },
    clearSnowApplicationGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchSnowApplication.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchSnowApplication.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchSnowApplication.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getSnowApplicationById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getSnowApplicationById.fulfilled.type]: (
      state,
      action: PayloadAction<ISnowApplication>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getSnowApplicationById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveSnowApplication.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveSnowApplication.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveSnowApplication.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Process Data
    [processDataSnow.pending.type]: (state) => {
      state.processData.loading = true;
      state.processData.messages = [];
    },
    [processDataSnow.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.processData.loading = false;
      state.processData.hasErrors = false;
      state.processData.messages = action.payload.messages;
    },
    [processDataSnow.rejected.type]: (state) => {
      state.processData.loading = false;
      state.processData.hasErrors = true;
    },

    // Delete
    [deleteSnowApplication.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteSnowApplication.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteSnowApplication.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const snowApplicationSelector = (state: RootState) => state.snowApplication;

// Actions
export const {
  clearSnowApplication,
  clearSnowApplicationMessages,
  clearSnowApplicationGetById,
  setTableColumnSelection,
} = snowApplicationSlice.actions;

// The reducer
export default snowApplicationSlice.reducer;
